import { useEffect, useState } from 'react';

import classNames from 'classnames';
import format from 'date-fns-tz/format';

import {
  useAiAssistantHistoryQuery,
  useAiAssistantQuotasQuery,
  useAiAssistantStatusQuery,
  useUserSqrvBalance,
} from '@query';

import { nextProjectUrl } from '@shared/constants/urls';
import { formatNumberLocalized } from '@shared/helpers/formatters';

import { ExternalLink } from '@ui-uikit/lib/components/common/ExternalLink';
import { InfoTooltip } from '@ui-uikit/lib/components/common/Tooltip';
import {
  CircleProgress1,
  CircleProgress2,
  CircleProgress3,
  CircleProgress4,
} from '@ui-uikit/lib/components/icons/mono';
import { ArrowLeft } from '@ui-uikit/lib/components/icons/mono';

import { useNavLayout } from '../NavLayout';

const IconForBlock = ({ index }: { index: number }) => {
  if (index === 0) {
    return <CircleProgress1 className="icon icon-button-sm" />;
  }

  if (index === 1) {
    return <CircleProgress2 className="icon icon-button-sm" />;
  }

  if (index === 2) {
    return <CircleProgress3 className="icon icon-button-sm" />;
  }

  return <CircleProgress4 className="icon icon-button-sm" />;
};

const Creadits = ({ backToChat, isFocused }: { isFocused: boolean; backToChat: () => void }) => {
  const [activeTab, setActiveTab] = useState<'credits' | 'history'>('credits');

  const { user } = useNavLayout();

  const { data, refetch } = useAiAssistantStatusQuery({ enabled: !!user });
  const { data: quotas = [] } = useAiAssistantQuotasQuery({ enabled: !!user });
  const { data: histories = [], refetch: refetchHistories } = useAiAssistantHistoryQuery({
    enabled: !!user,
  });
  const { data: sqrvBalance } = useUserSqrvBalance();

  useEffect(() => {
    if (activeTab === 'history') {
      refetchHistories();
    }
  }, [activeTab]);

  useEffect(() => {
    if (isFocused) {
      refetch();
    }
  }, [isFocused]);

  return (
    <div className="w-full flex flex-col h-full">
      <div className="p-3 border-b border-base-border-light">
        <p className="text-caption-sm-a cursor-pointer" onClick={backToChat}>
          <ArrowLeft className="icon-xs" />
          Back to Chat
        </p>
      </div>
      <div className="flex flex-col items-center w-full max-h-full p-6 overflow-y-auto py-6">
        <div className="flex justify-between w-full gap-2 pb-8">
          <div className="flex flex-col border border-base-border-light w-full rounded-xl p-4 gap-2">
            <p className="text-base-text-tertiary text-caption-xs-a">
              Credits <InfoTooltip message="Tooltip text" />
            </p>
            <p className="text-h3-a">
              {formatNumberLocalized(
                (data?.usage.creditsLimit || 0) - (data?.usage.creditsUsed || 0) || 0,
              )}{' '}
              of {formatNumberLocalized(data?.usage.creditsLimit || 0)} left
            </p>
          </div>
          <div className="flex flex-col border border-base-border-light w-full rounded-xl p-4 gap-2">
            <p className="text-base-text-tertiary text-caption-xs-a">
              SQRv <InfoTooltip message="Tooltip text" />
            </p>
            <p className="text-h3-a">{formatNumberLocalized(sqrvBalance?.score || 0)}</p>
          </div>
        </div>
        <div className="w-full">
          <div className="flex flex-row gap-10 overflow-auto hide-scrollbar border-b border-neutral-60">
            <button
              className={classNames('pb-6 font-semibold text-nowrap', {
                'border-b border-neutral-700': activeTab === 'credits',
              })}
              onClick={() => setActiveTab('credits')}
            >
              Get Credits
            </button>
            <button
              className={classNames('pb-6 font-semibold text-nowrap', {
                'border-b border-neutral-700': activeTab === 'history',
              })}
              onClick={() => setActiveTab('history')}
            >
              Balance History
            </button>
          </div>
          {activeTab === 'credits' && (
            <div className="w-full">
              <div className="py-6">
                <p className="text-caption-sm-strong-a">
                  Stake SQR to earn SQRv points and unlock more monthly Mystic AI Credits
                </p>
                <p className="text-caption-xs-a">
                  The more SQR you stake, the more Mystic AI monthly credits you’ll receive.
                </p>
              </div>
              <div>
                <p className="text-caption-sm-strong-a pb-4">SQRv Points Levels</p>
                <div className="flex justify-between gap-3 pb-4 flex-wrap">
                  {quotas?.map((block, index) => (
                    <div
                      key={index}
                      className="border border-base-border-light p-4 rounded-xl flex flex-col gap-2 w-[calc(50%_-_6px)]"
                    >
                      <IconForBlock index={index} />
                      <div>
                        <p className="text-caption-xs-strong-a">
                          {formatNumberLocalized(block.monthCredits)} Credits
                        </p>
                        <span className="text-caption-xs-a text-base-text-tertiary">Monthly</span>
                      </div>
                      <p className="text-caption-sm-a">
                        {formatNumberLocalized(block.minSQRv)} SQRv Holders
                      </p>
                    </div>
                  ))}
                </div>
                <ExternalLink
                  href={nextProjectUrl.staking('/')}
                  className="msq-btn msq-btn-primary msq-btn-lg"
                >
                  Stake SQR
                </ExternalLink>
              </div>
            </div>
          )}
          {activeTab === 'history' && (
            <div className="w-full pt-6">
              <div className="grid grid-cols-2 border-b border-base-border-light pb-2 w-full">
                <p className="text-caption-xs-a text-base-text-tertiary">Amount</p>
                <p className="text-caption-xs-a text-base-text-tertiary">Date</p>
              </div>
              {histories?.map((history) => (
                <div
                  className="grid grid-cols-2 border-b border-base-border-light py-4 w-full"
                  key={`${history.creditsUsed}_${history.date}`}
                >
                  <p>{history.creditsUsed}</p>
                  <p>{format(new Date(history.date), 'd MMM yyyy')}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Creadits;
